import { nriInsightData } from "../../data/nriTaxData";
import { Quote } from "lucide-react";

export const NriInsightSection = () => (
  <section id="nri-insight" className="relative py-28" style={{ background: "#252422" }}>
    <div className="absolute top-0 left-0 right-0 h-px bg-[#3f4816]/30" />
    <div className="max-w-[87.5rem] mx-auto px-10">

      {/* Heading — hero pattern */}
      <div className="mb-14">
        <span className="inline-block text-[#888680] text-xs font-medium uppercase tracking-[0.2em] mb-5" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
          {nriInsightData.sectionLabel}
        </span>
        <p className="text-[#888680]/60 font-semibold uppercase mb-1" style={{ fontFamily: "'Inter', Arial, sans-serif", fontSize: "clamp(0.85rem, 0.7rem + 0.8vw, 1.1rem)", letterSpacing: "-0.01em" }}>
          600,000 consultants in India.
        </p>
        <h2 className="font-black uppercase leading-tight" style={{ fontFamily: "'Inter', Arial, sans-serif", fontSize: "clamp(1.8rem, 1.4rem + 2.5vw, 3.5rem)", letterSpacing: "-0.03em" }}>
          <span className="text-[#d9fb06]">Almost none</span>
          <span className="text-[#888680]/50"> have filed</span>
          <br />
          <span className="text-[#dfddd6]">for someone </span>
          <span className="text-[#888680]/50">living abroad.</span>
        </h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 mb-14">
        {/* Short punchy body */}
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-3">
            <p className="text-[#dfddd6] font-bold text-lg leading-snug" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
              NRI tax requires expertise on{" "}
              <span className="text-[#d9fb06]">both sides of the border.</span>
            </p>
            <p className="text-[#888680] text-sm leading-relaxed" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
              Your India consultant sees only the Indian tax side. They have never had to navigate the{" "}
              <span className="text-[#dfddd6] font-semibold">DTAA treaty, TRC requirements, FEMA rules</span>, or the{" "}
              <span className="text-[#dfddd6] font-semibold">RNOR return window</span> for someone living outside India.
            </p>
            <p className="text-[#888680] text-sm" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
              That is a{" "}
              <span className="text-[#dfddd6] font-bold">specific expertise.</span>{" "}
              Not what you get for{" "}
              <span className="text-[#d9fb06] font-bold">₹4,000.</span>
            </p>
          </div>
        </div>

        {/* Comparison Table */}
        <div className="bg-[#1a1c1b] border border-[#3f4816]/15 rounded-sm overflow-hidden">
          <div className="grid grid-cols-3 bg-[#302f2c]/70 border-b border-[#3f4816]/20">
            <div className="px-4 py-3 border-r border-[#3f4816]/15">
              <span className="text-[#888680]/40 text-[10px] font-semibold uppercase tracking-wider" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>Criteria</span>
            </div>
            <div className="px-3 py-3 border-r border-[#3f4816]/15 text-center">
              <span className="text-[#888680]/50 text-[10px] font-semibold uppercase tracking-wider" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>India</span>
            </div>
            <div className="px-3 py-3 text-center bg-[#d9fb06]/5 border-l border-[#d9fb06]/10">
              <span className="text-[#d9fb06] text-[10px] font-black uppercase tracking-wider" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>CA Rahul</span>
            </div>
          </div>

          {nriInsightData.comparisonRows.map((row, i) => (
            <div key={i} className={`grid grid-cols-3 ${i < nriInsightData.comparisonRows.length - 1 ? "border-b border-[#3f4816]/10" : ""}`}>
              <div className="px-4 py-3 border-r border-[#3f4816]/10">
                <p className="text-[#888680] text-[11px] leading-snug" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>{row.criterion}</p>
              </div>
              <div className="px-3 py-3 border-r border-[#3f4816]/10 flex items-center justify-center">
                <span className={`text-xs font-semibold text-center leading-snug ${row.them === "✅" ? "text-[#888680]/50" : row.them === "❌" ? "text-red-400" : "text-[#888680]/40 text-[10px]"}`} style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
                  {row.them}
                </span>
              </div>
              <div className="px-3 py-3 flex items-center justify-center bg-[#d9fb06]/5 border-l border-[#d9fb06]/10">
                <span className="text-[#d9fb06] text-xs font-bold text-center leading-snug" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>{row.us}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pull Quote — key words highlighted */}
      <div className="max-w-3xl mx-auto text-center">
        <Quote size={22} className="text-[#d9fb06]/20 mx-auto mb-4" />
        <p
          className="font-black leading-snug"
          style={{
            fontFamily: "'Inter', Arial, sans-serif",
            fontSize: "clamp(1.1rem, 0.9rem + 1vw, 1.6rem)",
            letterSpacing: "-0.02em",
          }}
        >
          <span className="text-[#888680]/60">"There are </span>
          <span className="text-[#d9fb06]">600,000+</span>
          <span className="text-[#888680]/60"> tax consultants in India.</span>
          <br />
          <span className="text-[#dfddd6]/50">I'm </span>
          <span className="text-[#dfddd6] font-black">not competing</span>
          <span className="text-[#dfddd6]/50"> with them.</span>
          <br />
          <span className="text-[#888680]/60">I'm </span>
          <span className="text-[#d9fb06]">doing what they can't</span>
          <span className="text-[#888680]/60"> —</span>
          <br />
          <span className="text-[#888680]/60">from the </span>
          <span className="text-[#dfddd6]">other side of the border."</span>
        </p>
      </div>
    </div>
  </section>
);
